// src/firebase.js

import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, inMemoryPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getMessaging } from 'firebase/messaging';
import { getFunctions } from 'firebase/functions';
// Note: Do NOT statically import 'firebase/analytics' to keep Node/Jest safe.

// Firebase configuration using environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Environment guards
const isBrowser = typeof window !== 'undefined';
const isTest = process.env.NODE_ENV === 'test';
const isOnline = typeof navigator !== 'undefined' ? navigator.onLine !== false : true;

// Messaging (safe try/catch remains)
let messaging = null;
try {
  messaging = getMessaging(app);
} catch (_) {
  messaging = null;
}

// Cloud Functions: initialize in browser and also during Cypress runs
let functions = null;
const isCypress = isBrowser && typeof window !== 'undefined' && !!window.Cypress;
if (isBrowser && (!isTest || isCypress)) {
  functions = getFunctions(app, 'us-central1');
}

// Initialize Analytics dynamically (browser-only). Resolves to the analytics instance or null.
const analyticsReady = (async () => {
  try {
    const isCypress = isBrowser && typeof window !== 'undefined' && !!window.Cypress;
    if (!isBrowser || isTest || isCypress || !firebaseConfig.measurementId || !isOnline) return null;
    const mod = await import('firebase/analytics');
    const supported = await mod.isSupported();
    if (!supported) return null;
    return mod.getAnalytics(app);
  } catch (_) {
    return null;
  }
})();

// Set authentication persistence
setPersistence(auth, isCypress ? inMemoryPersistence : browserLocalPersistence).catch((error) => {
  console.error('Error setting persistence:', error);
});

// Register service worker for messaging (real browser; allow dev & prod; skip Jest)
// Avoid service worker registration during Cypress runs
if (isBrowser && 'serviceWorker' in navigator && !process.env.JEST_WORKER_ID && messaging && !isCypress) {
  navigator.serviceWorker.register('/firebase-messaging-sw.js')
    .then(() => {})
    .catch(() => {});
}

export { auth, db, storage, messaging, functions, analyticsReady };